import { useState, useCallback } from "react";
import {
  validateEmail,
  validatePhone,
  validateName,
  validateTitle,
  validateRequired,
  validateMinLength,
  validateMaxLength,
  validatePattern,
  ValidationResult,
} from "utils/validation";

/**
 * Validation options for input fields
 */
export interface ValidationOptions {
  /** Custom error message to display */
  customError?: string;
  /** Minimum length for text fields */
  minLength?: number;
  /** Maximum length for text fields */
  maxLength?: number;
  /** Custom regex pattern to validate against */
  pattern?: RegExp;
  /** Field label for dynamic error messages (e.g., "First name", "Email address") */
  fieldLabel?: string;
  /** Callback function to execute on validation error */
  onError?: (error: string) => void;
  /** Callback function to execute on validation success */
  onSuccess?: () => void;
}

/**
 * Field validation configuration
 */
interface FieldValidation {
  value: string;
  type: "email" | "phone" | "name" | "title" | "required" | "custom";
  options?: ValidationOptions;
}

/**
 * Hook for managing input validation
 * @example
 * const { validateField, validateMultipleFields, errors } = useInputValidation();
 *
 * // Validate single field
 * validateField({
 *   fieldName: "email",
 *   value: email,
 *   type: "email",
 *   options: {
 *     onError: (error) => toast.error(error),
 *     onSuccess: () => {}
 *   }
 * });
 *
 * // Validate multiple fields
 * const isValid = validateMultipleFields({
 *   firstName: { value: firstName, type: "name" },
 *   email: { value: email, type: "email" },
 *   phone: { value: phone, type: "phone" }
 * });
 */
export const useInputValidation = () => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Clear error for a specific field
   */
  const clearError = useCallback((fieldName: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  /**
   * Clear all errors
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * Set error for a specific field
   */
  const setError = useCallback((fieldName: string, error: string) => {
    setErrors((prev) => ({ ...prev, [fieldName]: error }));
  }, []);

  /**
   * Validate a single field
   * @param params - Validation parameters
   * @param params.fieldName - Name of the field (used for error tracking)
   * @param params.value - Value to validate
   * @param params.type - Type of validation to perform
   * @param params.options - Additional validation options
   * @returns true if validation passed, false otherwise
   */
  const validateField = useCallback(
    ({
      fieldName,
      value,
      type,
      options,
    }: {
      fieldName: string;
      value: string;
      type: FieldValidation["type"];
      options?: ValidationOptions;
    }): boolean => {
      let result: ValidationResult;

      switch (type) {
        case "email":
          result = validateEmail({
            email: value,
            customError: options?.customError,
          });
          break;
        case "phone":
          result = validatePhone({
            phone: value,
            customError: options?.customError,
          });
          break;
        case "name":
          result = validateName({
            name: value,
            minLength: options?.minLength,
            customError: options?.customError,
            fieldLabel: options?.fieldLabel,
          });
          break;
        case "title":
          result = validateTitle({
            title: value,
            minLength: options?.minLength,
            customError: options?.customError,
            fieldLabel: options?.fieldLabel,
          });
          break;
        case "required":
          result = validateRequired({
            value,
            customError: options?.customError,
          });
          break;
        case "custom":
          if (!options?.pattern) {
            throw new Error("Pattern is required for custom validation");
          }
          result = validatePattern({
            value,
            pattern: options.pattern,
            customError: options?.customError,
          });
          break;
        default:
          result = { isValid: true };
      }

      // Additional length validations if specified (not for 'name' or 'title' types as they handle minLength internally)
      if (result.isValid && options?.minLength && type !== "name" && type !== "title") {
        result = validateMinLength({
          value,
          minLength: options.minLength,
          customError: options?.customError,
        });
      }

      if (result.isValid && options?.maxLength) {
        result = validateMaxLength({
          value,
          maxLength: options.maxLength,
          customError: options?.customError,
        });
      }

      // Handle validation result
      if (result.isValid) {
        clearError(fieldName);
        options?.onSuccess?.();
        return true;
      } else {
        if (result.error) {
          setError(fieldName, result.error);
          options?.onError?.(result.error);
        }
        return false;
      }
    },
    [clearError, setError]
  );

  /**
   * Validate multiple fields at once
   * @param fields - Object containing field configurations
   * @returns true if all validations passed, false otherwise
   */
  const validateMultipleFields = useCallback(
    (fields: Record<string, FieldValidation>): boolean => {
      let allValid = true;
      const newErrors: Record<string, string> = {};

      Object.entries(fields).forEach(([fieldName, config]) => {
        let result: ValidationResult;

        switch (config.type) {
          case "email":
            result = validateEmail({
              email: config.value,
              customError: config.options?.customError,
            });
            break;
          case "phone":
            result = validatePhone({
              phone: config.value,
              customError: config.options?.customError,
            });
            break;
          case "name":
            result = validateName({
              name: config.value,
              minLength: config.options?.minLength,
              customError: config.options?.customError,
              fieldLabel: config.options?.fieldLabel,
            });
            break;
          case "title":
            result = validateTitle({
              title: config.value,
              minLength: config.options?.minLength,
              customError: config.options?.customError,
              fieldLabel: config.options?.fieldLabel,
            });
            break;
          case "required":
            result = validateRequired({
              value: config.value,
              customError: config.options?.customError,
            });
            break;
          case "custom":
            if (!config.options?.pattern) {
              throw new Error("Pattern is required for custom validation");
            }
            result = validatePattern({
              value: config.value,
              pattern: config.options.pattern,
              customError: config.options?.customError,
            });
            break;
          default:
            result = { isValid: true };
        }

        // Additional length validations (not for 'name' or 'title' types as they handle minLength internally)
        if (result.isValid && config.options?.minLength && config.type !== "name" && config.type !== "title") {
          result = validateMinLength({
            value: config.value,
            minLength: config.options.minLength,
            customError: config.options?.customError,
          });
        }

        if (result.isValid && config.options?.maxLength) {
          result = validateMaxLength({
            value: config.value,
            maxLength: config.options.maxLength,
            customError: config.options?.customError,
          });
        }

        if (!result.isValid) {
          allValid = false;
          if (result.error) {
            newErrors[fieldName] = result.error;
            config.options?.onError?.(result.error);
          }
        } else {
          config.options?.onSuccess?.();
        }
      });

      setErrors(newErrors);
      return allValid;
    },
    []
  );

  /**
   * Get error message for a specific field
   */
  const getError = useCallback(
    (fieldName: string): string | undefined => {
      return errors[fieldName];
    },
    [errors]
  );

  /**
   * Check if a field has an error
   */
  const hasError = useCallback(
    (fieldName: string): boolean => {
      return !!errors[fieldName];
    },
    [errors]
  );

  return {
    validateField,
    validateMultipleFields,
    errors,
    clearError,
    clearAllErrors,
    setError,
    getError,
    hasError,
  };
};
